import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Settings, Lock, ArrowLeft } from 'lucide-react';
import FollowListModal from './FollowListModal';
import SafetyMenu from './SafetyMenu';
import ReportModal from './ReportModal';

const Avatar = ({ src, name, size = 96 }) => {
  if (src) {
    return (
      <img
        src={src}
        alt={name}
        className="rounded-full object-cover"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full bg-gradient-to-tr from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold"
      style={{ width: size, height: size, fontSize: size * 0.4 }}
    >
      {(name || '?').charAt(0).toUpperCase()}
    </div>
  );
};

const Profile = ({
  isRTL,
  user, // current user
  token,
  API,
  viewUserId, // null/undefined = own profile
  onBack,
  onOpenSettings,
  onOpenProfile, // navigate to another user's profile
  onMessage, // (otherUserId) => open chat
  onCreatePost, // own-profile only: switch to Create tab from 0-posts CTA
  t,
}) => {
  const isOwn = !viewUserId || viewUserId === user.id;
  const targetId = isOwn ? user.id : viewUserId;

  const [target, setTarget] = useState(isOwn ? user : null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [followStatus, setFollowStatus] = useState({
    following: false,
    followed_by: false,
    blocked: false,
    blocked_by: false,
  });
  const [busy, setBusy] = useState(false);
  const [modalType, setModalType] = useState(null); // 'followers' | 'following'
  const [reportOpen, setReportOpen] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const toggleBlock = async () => {
    const willBlock = !followStatus.blocked;
    if (willBlock && !window.confirm(t('blockConfirm'))) return;
    if (!willBlock && !window.confirm(t('unblockConfirm'))) return;
    try {
      if (willBlock) {
        await axios.post(`${API}/users/${targetId}/block`, {}, { headers });
      } else {
        await axios.delete(`${API}/users/${targetId}/block`, { headers });
      }
      setFollowStatus((s) => ({
        ...s,
        blocked: willBlock,
        following: willBlock ? false : s.following,
      }));
    } catch {
      alert(t('error'));
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const reqs = [
        axios.get(`${API}/posts/user/${targetId}`, { headers }),
      ];
      if (isOwn) {
        reqs.push(axios.get(`${API}/auth/me`, { headers }));
      } else {
        reqs.push(axios.get(`${API}/users/${targetId}`, { headers }));
        reqs.push(axios.get(`${API}/users/${targetId}/follow-status`, { headers }));
      }
      const results = await Promise.all(reqs);
      setPosts(results[0].data || []);
      setTarget(results[1].data);
      if (!isOwn && results[2]) {
        setFollowStatus(results[2].data);
      }
    } catch (e) {
      console.error('Profile load error', e);
    } finally {
      setLoading(false);
    }
  }, [API, targetId, token, isOwn]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleFollow = async () => {
    if (busy) return;
    setBusy(true);
    const wasFollowing = followStatus.following;
    setFollowStatus((s) => ({ ...s, following: !wasFollowing }));
    setTarget((u) =>
      u
        ? { ...u, followers_count: Math.max(0, (u.followers_count || 0) + (wasFollowing ? -1 : 1)) }
        : u
    );
    try {
      if (wasFollowing) {
        await axios.delete(`${API}/users/${targetId}/follow`, { headers });
      } else {
        await axios.post(`${API}/users/${targetId}/follow`, {}, { headers });
      }
    } catch (e) {
      // revert
      setFollowStatus((s) => ({ ...s, following: wasFollowing }));
      setTarget((u) =>
        u
          ? { ...u, followers_count: Math.max(0, (u.followers_count || 0) + (wasFollowing ? 1 : -1)) }
          : u
      );
    } finally {
      setBusy(false);
    }
  };

  if (loading || !target) {
    return (
      <div
        data-testid="profile-loading"
        role="status"
        aria-live="polite"
        aria-label={t('loadingContent')}
        className="min-h-screen bg-gray-50"
      >
        <div className="max-w-2xl mx-auto px-4 pt-5 pb-24">
          <div className="flex items-center gap-5 mb-4">
            <div className="circleo-skeleton w-22 h-22 rounded-full" style={{ width: 88, height: 88 }} />
            <div className="flex-1 grid grid-cols-3 gap-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex flex-col items-center gap-2">
                  <div className="circleo-skeleton h-4 w-8" />
                  <div className="circleo-skeleton h-3 w-12" />
                </div>
              ))}
            </div>
          </div>
          <div className="circleo-skeleton h-3 w-24 mb-2" />
          <div className="circleo-skeleton h-3 w-3/4 mb-5" />
          <div className="circleo-skeleton h-9 w-full rounded-lg mb-4" />
          <div className="grid grid-cols-3 gap-1">
            {[0, 1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="circleo-skeleton aspect-square rounded-none" />
            ))}
          </div>
        </div>
        <span className="sr-only-circleo">{t('loading')}</span>
      </div>
    );
  }

  const isPrivateAndBlocked = !isOwn && target.profile_privacy === 'private' && !followStatus.following;
  const displayName =
    target.display_preference === 'nickname' && target.nickname ? target.nickname : target.name;

  return (
    <div data-testid="profile-screen" dir={isRTL ? 'rtl' : 'ltr'} className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-30 bg-white border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          {onBack ? (
            <button
              onClick={onBack}
              data-testid="profile-back"
              aria-label={t('back')}
              className="flex items-center gap-1 text-purple-600 font-medium p-2 -ms-2 min-h-[40px]"
            >
              <ArrowLeft aria-hidden="true" className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
              <span className="text-sm">{t('back')}</span>
            </button>
          ) : (
            <div className="w-12" />
          )}
          <h1 className="text-base font-bold text-gray-900 truncate max-w-[60%] text-center">
            {target.nickname ? `@${target.nickname}` : displayName}
          </h1>
          {isOwn ? (
            <button
              onClick={onOpenSettings}
              data-testid="profile-open-settings"
              className="p-2 min-w-[40px] min-h-[40px] inline-flex items-center justify-center rounded-full hover:bg-gray-100 text-gray-700"
              aria-label={t('settings')}
            >
              <Settings aria-hidden="true" className="w-5 h-5" />
            </button>
          ) : (
            <SafetyMenu
              testid="profile-safety-menu"
              t={t}
              isRTL={isRTL}
              size="md"
              align="end"
              items={[
                {
                  key: 'block',
                  label: followStatus.blocked ? t('unblock') : t('block'),
                  danger: !followStatus.blocked,
                  onClick: toggleBlock,
                  testid: 'profile-safety-block',
                },
                {
                  key: 'report',
                  label: t('reportUser'),
                  danger: true,
                  onClick: () => setReportOpen(true),
                  testid: 'profile-safety-report',
                },
              ]}
            />
          )}
        </div>
      </header>

      <div className="max-w-2xl mx-auto px-4 pt-5 pb-24">
        <section className="flex items-center gap-5 mb-4">
          <Avatar src={target.profile_picture_url} name={displayName} size={88} />
          <div className="flex-1 grid grid-cols-3 text-center">
            <button
              data-testid="profile-stats-posts"
              className="flex flex-col"
              type="button"
            >
              <span className="text-lg font-bold text-gray-900">{posts.length}</span>
              <span className="text-xs text-gray-500">{t('posts')}</span>
            </button>
            <button
              type="button"
              data-testid="profile-stats-followers"
              onClick={() => setModalType('followers')}
              className="flex flex-col"
            >
              <span className="text-lg font-bold text-gray-900">{target.followers_count || 0}</span>
              <span className="text-xs text-gray-500">{t('followers')}</span>
            </button>
            <button
              type="button"
              data-testid="profile-stats-following"
              onClick={() => setModalType('following')}
              className="flex flex-col"
            >
              <span className="text-lg font-bold text-gray-900">{target.following_count || 0}</span>
              <span className="text-xs text-gray-500">{t('following')}</span>
            </button>
          </div>
        </section>

        <section className="mb-4">
          <h2 className="text-sm font-semibold text-gray-900">{displayName}</h2>
          {target.bio && <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{target.bio}</p>}
        </section>

        <section className="mb-5">
          {isOwn ? (
            <button
              onClick={onOpenSettings}
              data-testid="profile-edit-button"
              className="w-full py-2 rounded-lg border border-gray-200 bg-white text-sm font-semibold hover:bg-gray-50"
            >
              {t('editProfile')}
            </button>
          ) : followStatus.blocked ? (
            <div
              data-testid="profile-blocked-notice"
              className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg text-center"
            >
              {t('youBlockedNotice')}
            </div>
          ) : followStatus.blocked_by ? (
            <div
              data-testid="profile-blocked-by-notice"
              className="bg-gray-50 border border-gray-200 text-gray-700 text-sm px-3 py-2 rounded-lg text-center"
            >
              {t('blockedByThemNotice')}
            </div>
          ) : (
            <div className="flex gap-2">
              <button
                onClick={toggleFollow}
                disabled={busy}
                data-testid="profile-follow-button"
                className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                  followStatus.following
                    ? 'border border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                }`}
              >
                {followStatus.following
                  ? t('unfollow')
                  : followStatus.followed_by
                  ? t('followBack')
                  : t('follow')}
              </button>
              {onMessage && (
                <button
                  onClick={() => onMessage(targetId)}
                  data-testid="profile-message-button"
                  className="flex-1 py-2 rounded-lg text-sm font-semibold border border-gray-200 bg-white text-gray-800 hover:bg-gray-50"
                >
                  {t('messageBtn')}
                </button>
              )}
            </div>
          )}
        </section>

        {isPrivateAndBlocked ? (
          <section
            data-testid="profile-private-state"
            className="bg-white border border-gray-100 rounded-xl px-4 py-10 text-center text-gray-500"
          >
            <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
              <Lock className="w-5 h-5 text-gray-500" />
            </div>
            <p className="font-semibold text-gray-800">{t('privateProfile')}</p>
            <p className="text-sm mt-1">{t('privateProfileHint')}</p>
          </section>
        ) : posts.length === 0 ? (
          <section
            data-testid="profile-empty-posts"
            className="bg-white border border-gray-100 rounded-xl px-4 py-12 text-center text-gray-500"
          >
            <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-tr from-purple-100 to-pink-100 flex items-center justify-center text-purple-500 text-2xl font-bold mb-3">
              ◎
            </div>
            <p className="text-sm font-semibold text-gray-800">{t('noPostsYet')}</p>
            {isOwn && (
              <>
                <p className="text-xs text-gray-500 mt-1 mb-4">{t('noPostsYetHint')}</p>
                {onCreatePost && (
                  <button
                    type="button"
                    onClick={onCreatePost}
                    data-testid="profile-empty-create-cta"
                    className="px-5 py-2 rounded-full bg-gradient-to-r from-purple-600 to-pink-500 text-white text-sm font-semibold shadow"
                  >
                    {t('noPostsYetCTA')}
                  </button>
                )}
              </>
            )}
          </section>
        ) : (
          <section data-testid="profile-posts-grid" className="grid grid-cols-3 gap-1">
            {posts.map((p) => (
              <div key={p.id} className="aspect-square bg-gray-100 overflow-hidden">
                {p.media_type === 'video' ? (
                  <video src={p.media_url} className="w-full h-full object-cover" />
                ) : (
                  <img src={p.media_url} alt="" className="w-full h-full object-cover" />
                )}
              </div>
            ))}
          </section>
        )}
      </div>

      <FollowListModal
        open={!!modalType}
        type={modalType || 'followers'}
        userId={targetId}
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
        onClose={() => setModalType(null)}
        onOpenProfile={(uid) => {
          if (onOpenProfile) onOpenProfile(uid);
        }}
      />

      <ReportModal
        open={reportOpen}
        onClose={() => setReportOpen(false)}
        targetType="user"
        targetId={targetId}
        token={token}
        API={API}
        t={t}
        isRTL={isRTL}
      />
    </div>
  );
};

export default Profile;
